import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  ErrorText,
  StyledLabel,
  StyledOption,
  StyledOptionsList,
  StyledOptionsWrapper,
  StyledSelectButton,
  StyledSelectWrapper,
} from './select.style';

const SelectComponent = ({
  options = [],
  onSelect = () => {},
  label = '',
  placeholder = 'Select an option',
  sizevalue = 'normal',
  disabled = false,
  error = false,
  errorText = '',
  defaultValue = null,
  id = 'select-component',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const selectRef = useRef(null);
  const optionsRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Manage focus for keyboard navigation
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current) {
      const optionElements = optionsRef.current.querySelectorAll('[role="option"]');
      if (optionElements[focusedIndex]) {
        optionElements[focusedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, focusedIndex]);

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setFocusedIndex(0);
      }
    }
  };

  // Handle option selection
  const handleSelect = (option) => {
    setSelected(option);
    onSelect(option);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex((prevIndex) => (prevIndex + 1) % options.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(options.length - 1);
        } else {
          setFocusedIndex((prevIndex) => (prevIndex - 1 + options.length) % options.length);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && focusedIndex >= 0 && options[focusedIndex]) {
          handleSelect(options[focusedIndex]);
        } else if (!isOpen) {
          toggleDropdown();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
        }
        break;
      default:
        break;
    }
  };

  // Handle option keyboard events
  const handleOptionKeyDown = (e, option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(option);
    }
  };

  // Get display text
  const getDisplayText = () => {
    if (selected) {
      return selected.label || selected.name || selected.procedureName;
    }
    return placeholder;
  };

  // Generate IDs for accessibility
  const labelId = `${id}-label`;
  const listboxId = `${id}-listbox`;

  return (
    <StyledSelectWrapper ref={selectRef}>
      {label && (
        <StyledLabel id={labelId} htmlFor={id} sizevalue={sizevalue} style={{ fontSize: '14px' }}>
          {label}
        </StyledLabel>
      )}

      <StyledSelectButton
        ref={buttonRef}
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={label ? `${labelId} ${id}` : id}
        aria-controls={isOpen ? listboxId : undefined}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        isOpen={isOpen}
        sizevalue={sizevalue}
        color={error ? 'error' : undefined}
      >
        <span style={{ fontSize: '14px' }}>{getDisplayText()}</span>
        <ChevronDown size={16} />
      </StyledSelectButton>

      {isOpen && !disabled && (
        <StyledOptionsWrapper>
          <StyledOptionsList
            ref={optionsRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
          >
            {options.length > 0 ? (
              options.map((option, index) => (
                <StyledOption
                  key={option.value || option._id || index}
                  role="option"
                  id={`${id}-option-${index}`}
                  tabIndex={focusedIndex === index ? 0 : -1}
                  aria-selected={selected?.value === option.value || selected?._id === option._id}
                  onClick={() => handleSelect(option)}
                  onKeyDown={(e) => handleOptionKeyDown(e, option)}
                  isSelected={selected?.value === option.value || selected?._id === option._id}
                  className={focusedIndex === index ? 'focused' : ''}
                  sizevalue={sizevalue}
                >
                  {option.icon && <span aria-hidden="true">{option.icon}</span>}
                  {option.label || option.name || option.procedureName}
                </StyledOption>
              ))
            ) : (
              <StyledOption
                role="option"
                tabIndex={-1}
                isSelected={false}
                sizevalue={sizevalue}
                aria-disabled="true"
              >
                No options available
              </StyledOption>
            )}
          </StyledOptionsList>
        </StyledOptionsWrapper>
      )}

      {error && errorText && <ErrorText>{errorText}</ErrorText>}
    </StyledSelectWrapper>
  );
};

export default SelectComponent;
